import React, {
  ReactElement,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import YouTube from 'react-youtube';
import { useForm } from 'react-hook-form';
import type YouTubePlayer from 'youtube-player';
import { useNavigate } from 'react-router-dom';

import COLORS from '~/constants/colors';
import VARIANTS from '~/constants/variants';
import { FONT_SIZES } from '~/constants/typography';
import urls from '~/urls';
import FramedView from '~/components/FramedView';
import Divider from '~/components/flow/Divider';
import TextInput from '~/components/inputs/TextInput';
import NumberInput from '~/components/inputs/NumberInput';
import Button from '~/components/buttons/Button';
import Announcement from '~/components/Announcement';
import Code from '~/components/flow/Code';
import TextArea from '~/components/inputs/TextArea';
import Link from '~/components/flow/Link';
import { stopEventEffects } from '~/utils';
import Title from '~/components/titles/Title';
import Header from '~/components/titles/Header';
import Subtext from '~/components/titles/Subtext';

import { TEMPO_NUMERATOR } from '../Arcade/components/GameForeground/constants/mechanics';
import { SONGS } from '../../examples';
import {
  GAME_MODES,
  DEFAULT_META,
  DEFAULT_SONG,
  DEFAULT_RHYTHM,
} from '../../constants/defaults';
import { PRECISIONS } from './constants';
import uris from '../../uris';
import { getVideoId } from '../../helpers';
import { setVideoMeta } from './services';
import { Session } from '../../models';
import OptionSelect from '../../components/OptionSelect';
import NoteVelocitySlider from '../../components/NoteVelocitySlider';
import SongIntervalSlider from './components/SongIntervalSlider';

interface StudioProps {
  session: Session;
  setSession: Dispatch<SetStateAction<Session>>;
  videoPlayer: YouTubePlayer;
  playerState: number;
  setPlayerState: Dispatch<SetStateAction<number>>;
}

function Studio({
  session,
  setSession,
  videoPlayer,
  playerState,
  setPlayerState,
}: StudioProps): ReactElement {
  const output = JSON.stringify({ ...session.song, rhythms: [session.rhythm] });
  const selectedSong = SONGS[session.songIdx] ?? DEFAULT_SONG;
  const selectedRhythm =
    selectedSong.rhythms[session.rhythmIdx] ?? DEFAULT_RHYTHM;
  const actualPlayerState = videoPlayer?.getPlayerState() ?? NaN; // Needed in certain checks to avoid concurrency issues

  // Form stuff
  const defaultValues = {
    ...DEFAULT_META,
    ...(session.songIdx === -1 && {
      source: session.song.source,
      bpm: session.song.bpm,
    }),
  };
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitSuccessful, errors },
  } = useForm({ defaultValues, reValidateMode: 'onSubmit' });
  const sourceOpts = { required: true, validate: (link) => !!getVideoId(link) };
  const bpmOpts = { required: true, min: 1 };
  const nameRef = useRef<HTMLInputElement>(null);
  const promoTextRef = useRef<HTMLTextAreaElement>(null);
  const promoLinkRef = useRef<HTMLInputElement>(null);
  const clearExtras = () => {
    if (nameRef.current) nameRef.current.value = '';
    if (promoTextRef.current) promoTextRef.current.value = '';
    if (promoLinkRef.current) promoLinkRef.current.value = '';
  };
  const getSafeBpm = (bpm) => Math.max(Math.round(Number(bpm)), 1);
  const handleChangeBpm = (event) => {
    stopEventEffects(event);
    setSession({
      ...session,
      song: { ...session.song, bpm: getSafeBpm(event.target.value) },
    });
  };

  // Handle adding or selecting song
  const cueVideo = (source) => {
    const newVideoId = getVideoId(source);

    if (newVideoId) {
      videoPlayer?.cueVideoById(newVideoId);
      setPlayerState(YouTube.PlayerState.UNSTARTED); // Needed for triggering `useEffect` on resubmit
    }
  };
  const handleSelectSong = (option) => {
    const newSelectedSong = SONGS[option.value] ?? DEFAULT_SONG;
    const newSelectedRhythm = newSelectedSong.rhythms[0] ?? DEFAULT_RHYTHM;

    clearExtras();
    setSession({
      ...session,
      songIdx: option.value,
      rhythmIdx: option.value >= 0 ? 0 : -1,
      song: { ...newSelectedSong },
      rhythm: { ...newSelectedRhythm },
    });
    cueVideo(newSelectedSong.source);
    reset();
  };
  const handleAddSong = (song) => {
    clearExtras();
    setSession({
      ...session,
      songIdx: -1,
      rhythmIdx: -1,
      song: { ...DEFAULT_SONG, ...song, bpm: getSafeBpm(song.bpm) },
      rhythm: { ...DEFAULT_RHYTHM },
    });
    cueVideo(song.source);
  };

  // Handle other interactions
  const disabled =
    playerState <= YouTube.PlayerState.UNSTARTED ||
    actualPlayerState === YouTube.PlayerState.UNSTARTED || // Additional check needed since `playerState` and `actualPlayerState` can desync
    (!session.song.source && !isSubmitSuccessful);
  const playerStartTime = session.videoStartTime / 1000;
  const playerEndTime = session.videoEndTime / 1000;
  const handleSelectRhythm = (option) => {
    const newRhythm = SONGS[session.songIdx].rhythms[option.value];

    clearExtras();
    setSession({
      ...session,
      rhythmIdx: option.value,
      rhythm: { ...newRhythm },
    });
    if (nameRef.current) nameRef.current.value = newRhythm.name;
    if (promoTextRef.current)
      promoTextRef.current.value = newRhythm.promoText ?? '';
    if (promoLinkRef.current)
      promoLinkRef.current.value = newRhythm.promoLink ?? '';
  };
  const getHandleExtra = (field) => (event) => {
    const newRhythm = { ...session.rhythm };

    stopEventEffects(event);
    if (!event.target.value && field !== 'name') delete newRhythm[field];
    else newRhythm[field] = event.target.value;
    setSession({ ...session, rhythm: newRhythm });
  };
  const handleInputEnter = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  };

  useEffect(() => {
    if (Number.isNaN(actualPlayerState)) return;
    if (actualPlayerState === YouTube.PlayerState.CUED) {
      const duration = Math.floor(videoPlayer.getDuration());
      const newSession = {
        ...session,
        duration,
        videoStartTime: 0,
        videoEndTime: duration * 1000,
      };

      setVideoMeta(newSession, setSession);
    } else if (playerState < YouTube.PlayerState.UNSTARTED)
      setError('source', {});
    else if (
      actualPlayerState === YouTube.PlayerState.UNSTARTED &&
      session.song.source
    )
      cueVideo(session.song.source);
  }, [playerState]);

  // Handle starting game
  const navigate = useNavigate();
  const handleStartGame = (gameMode) => (event) => {
    stopEventEffects(event);
    if (videoPlayer) {
      // Needed here for mobile not allowing autoplay
      videoPlayer.setVolume(100);
      videoPlayer.loadVideoById({
        videoId: getVideoId(session.song.source),
        startSeconds: playerStartTime,
        endSeconds: playerEndTime,
      });
      navigate(uris.arcade, { state: { gameMode } });
    }
  };

  // Timing alignment
  const roundTimings = (timings: number[]) => {
    const divisorTime =
      (TEMPO_NUMERATOR / session.song.bpm) * session.precision;
    const alignTiming = (t) => Math.round(t / divisorTime) * divisorTime;
    const isInsideSong = (t) => t >= 0 && t <= session.duration * 1000;
    const isOutsideRange = (t) =>
      (t < session.videoStartTime || t > session.videoEndTime) &&
      isInsideSong(t);
    const isInsideRange = (t) => !isOutsideRange(t) && isInsideSong(t);
    const alignedRange = timings.filter(isInsideRange).map(alignTiming);
    const merged = timings.filter(isOutsideRange).concat(alignedRange);

    return [...new Set(merged.sort((a, b) => a - b))];
  };
  const handleAlignment = (event) => {
    stopEventEffects(event);
    setSession({
      ...session,
      rhythm: {
        ...session.rhythm,
        timings: roundTimings(session.rhythm.timings),
      },
    });
  };

  // Styling
  const framedViewStyle = {
    width: 'min(45em, 82.5vw)',
  };
  const frameColors: [string, string] = [
    COLORS.waikawaGray,
    COLORS.waikawaGray,
  ];
  const levelStyle = {
    display: 'block',
    marginTop: '1em',
  };
  const actionLevelStyle = {
    ...levelStyle,
    fontSize: FONT_SIZES.action,
  };
  const levelRowSpaceStyle = {
    height: '0.25em',
  };
  const optionSelectStyle = {
    width: 'min(20em, 80vw)',
  };
  const textInputStyle = {
    width: 'min(320px, 75vw)',
  };
  const numberInputStyle = {
    width: 'min(314px, 73.4vw)',
  };
  const songIntervalLabelStyle = {
    margin: '0.25em 1em 0.25em',
    color: COLORS.dodgerBlue,
  };
  const buttonStyle = {
    width: '9em',
    margin: '0 0.25em 0.38em',
    boxShadow: `-0.1em 0.1em ${COLORS.black25}`,
    borderStyle: 'none',
    fontSize: FONT_SIZES.text,
  };
  const sliderStyle = {
    width: 'min(305px, 69vw)',
    minWidth: 0,
    margin: '0.25em 0.5em 0.5em 1em',
  };
  const textAreaStyle = {
    width: 'min(320px, 75vw)',
    height: '5em',
  };
  const outputStyle = {
    ...textAreaStyle,
    width: 'min(640px, 75vw)',
    height: '7.5em',
  };
  const alignOptionSelectStyle = {
    ...optionSelectStyle,
    width: 'min(9.5em, 80vw)',
    margin: '0 0.25em 0.38em',
  };
  const alignButtonStyle = {
    ...buttonStyle,
    margin: '0 0 0.38em 0.25em',
  };

  return (
    <div>
      <FramedView accent colors={frameColors} style={framedViewStyle}>
        <Announcement header="Warning">
          There is no persistence. If you refresh or change pages, your work
          will be lost!
        </Announcement>
        <Title>Studio</Title>
        <br />
        Enter a YouTube link and BPM or select an already added song. You can
        then record a new rhythm or review what you&apos;ve made. To record, tap
        the pad or press enter when you want a note to hit the receptor bar.
        Focus on timing relative to the song not the positioning. The note
        highway here only communicates a relative sense of timing. If you
        don&apos;t like a part, adjust the start and end times then use the
        alignment feature or click record to overwrite what&apos;s inclusively
        between. You can review specific parts similarly.
        <Divider />
        <Header>Song Selection</Header>
        <div style={levelStyle}>
          <OptionSelect
            placeholder="Select a song..."
            value={{
              value: session.songIdx,
              label: session.songIdx >= 0 ? selectedSong.title : 'New song...',
            }}
            onChange={handleSelectSong}
            options={[
              { value: -1, label: 'New song...' },
              ...SONGS.map((s, idx) => ({
                value: idx,
                label: `${s.title} by ${s.artist}`,
              })),
            ]}
            style={optionSelectStyle}
          />
        </div>
        {session.songIdx < 0 && (
          <form style={levelStyle}>
            <div style={actionLevelStyle}>
              Enter a YouTube link
              <div style={levelRowSpaceStyle} />
              <TextInput
                {...register('source', sourceOpts)}
                style={textInputStyle}
              />
              {errors.source && (
                <Subtext color={COLORS.mandy}>
                  Valid YouTube link required!
                </Subtext>
              )}
            </div>
            <div style={actionLevelStyle}>
              What is the song&apos;s BPM?
              <div style={levelRowSpaceStyle} />
              <NumberInput
                min={1}
                {...register('bpm', bpmOpts)}
                onChange={handleChangeBpm}
                style={numberInputStyle}
              />
              {errors.bpm && (
                <Subtext color={COLORS.mandy}>Valid BPM required!</Subtext>
              )}
            </div>
            <br />
            <Button
              variant={VARIANTS.warning}
              label="Load"
              onClick={handleSubmit(handleAddSong)}
              onTouchEnd={handleSubmit(handleAddSong)}
              style={buttonStyle}
            />
          </form>
        )}
        {session.songIdx >= 0 && (
          <div style={levelStyle}>
            <div style={levelStyle}>
              Start with an existing rhythm?
              <div style={levelRowSpaceStyle} />
              <OptionSelect
                placeholder={
                  selectedRhythm.name
                    ? 'Select a pattern...'
                    : 'No patterns available'
                }
                value={{
                  value: session.rhythmIdx,
                  label: selectedRhythm.name,
                }}
                onChange={handleSelectRhythm}
                options={selectedSong.rhythms.map((r, idx) => ({
                  value: idx,
                  label: r.name,
                }))}
                isSearchable={false}
                controlShouldRenderValue={!!selectedRhythm.name}
                disabled={!selectedRhythm.name}
                style={optionSelectStyle}
              />
            </div>
            <div style={levelStyle}>
              Here are the selected rhythm&apos;s timings
              <div style={levelRowSpaceStyle} />
              <TextArea
                readOnly
                value={JSON.stringify(selectedRhythm.timings)}
                style={textAreaStyle}
              />
            </div>
          </div>
        )}
        {!disabled && (
          <div style={levelStyle}>
            <Divider />
            <Header>Rhythm Recording</Header>
            {session.song.title && session.song.artist && (
              <Announcement variant={VARIANTS.warning}>
                The currently loaded song is{' '}
                <Code highlight={COLORS.white}>{session.song.title}</Code> by{' '}
                <Code highlight={COLORS.white}>{session.song.artist}</Code>!
              </Announcement>
            )}
            <div style={levelStyle}>
              What should we call your new rhythm?
              <div style={levelRowSpaceStyle} />
              <TextInput
                defaultValue={session.rhythm.name}
                onBlur={getHandleExtra('name')}
                onKeyDown={handleInputEnter}
                style={textInputStyle}
                ref={nameRef}
              />
            </div>
            <div style={levelStyle}>
              Which part of the song do you want to work with?
              <div style={levelRowSpaceStyle} />
              <Subtext style={songIntervalLabelStyle}>
                From <b>{playerStartTime}</b> seconds to <b>{playerEndTime}</b>{' '}
                seconds
              </Subtext>
              <SongIntervalSlider
                session={session}
                setSession={setSession}
                style={sliderStyle}
              />
            </div>
            <div style={levelStyle}>
              How fast do you want the notes to look?
              <div style={levelRowSpaceStyle} />
              <NoteVelocitySlider
                velocity={session.velocity}
                onChange={(velocity) => setSession({ ...session, velocity })}
                markLabel="Normal"
                markColor={COLORS.tundora}
                style={sliderStyle}
              />
            </div>
            <div style={levelStyle}>
              Feel free to add a shoutout!
              <div style={levelRowSpaceStyle} />
              <TextArea
                defaultValue={session.rhythm.promoText}
                onBlur={getHandleExtra('promoText')}
                onKeyDown={handleInputEnter}
                maxLength={280}
                style={textAreaStyle}
                ref={promoTextRef}
              />
            </div>
            <div style={levelStyle}>
              Do you want your shoutout to also be a link?
              <div style={levelRowSpaceStyle} />
              <TextInput
                defaultValue={session.rhythm.promoLink}
                onBlur={getHandleExtra('promoLink')}
                onKeyDown={handleInputEnter}
                style={textInputStyle}
                ref={promoLinkRef}
              />
              <br />
              <Subtext>Please keep things safe for work</Subtext>
            </div>
            <div style={levelStyle}>
              <Button
                label="Record"
                disabled={disabled}
                ignoreDisabledStyle
                onClick={handleStartGame(GAME_MODES.record)}
                onTouchEnd={handleStartGame(GAME_MODES.record)}
                style={buttonStyle}
              />
              <Button
                label="Review"
                disabled={disabled}
                ignoreDisabledStyle
                onClick={handleStartGame(GAME_MODES.review)}
                onTouchEnd={handleStartGame(GAME_MODES.review)}
                style={buttonStyle}
              />
            </div>
          </div>
        )}
      </FramedView>
      <br />
      <FramedView accent colors={frameColors} style={framedViewStyle}>
        <Title>Submission</Title>
        <br />
        To add your rhythm to the game, join my{' '}
        <Link href={urls.discordInvite}>Discord</Link> and post what&apos;s in
        this last box to <Code>#japanese</Code>. Sorry it&apos;s a bit low-tech
        right now. I&apos;ll add a database if enough people are interested.
        <div style={levelStyle}>
          <TextArea
            readOnly
            value={disabled ? '' : output}
            style={outputStyle}
          />
        </div>
        <Divider />
        <Header>Adjust Alignment</Header>
        <Announcement variant={VARIANTS.warning}>
          Alignment works relative to the loaded song&apos;s BPM inclusively
          between the start and end times set above. This action irrecoverably
          mutates your work. Timings that overlap or exist outside the loaded
          song&apos;s duration will be removed.
        </Announcement>
        <div style={levelStyle}>
          To what fraction of a beat would you like to align your timings?
          <div style={levelRowSpaceStyle} />
          <OptionSelect
            placeholder="Select a precision..."
            value={{
              value: session.precision,
              label: PRECISIONS.get(session.precision),
            }}
            onChange={(option) =>
              setSession({ ...session, precision: option.value })
            }
            options={Array.from(PRECISIONS).map(([v, l]) => ({
              value: v,
              label: l,
            }))}
            style={alignOptionSelectStyle}
          />
          <Button
            label="Align"
            variant={VARIANTS.warning}
            onClick={handleAlignment}
            onTouchEnd={handleAlignment}
            style={alignButtonStyle}
          />
        </div>
      </FramedView>
    </div>
  );
}

export default Studio;
